import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import roles from '../copy/roles';

const BASE_URL = 'https://www.hadleyswello.com';

// Extract all unique skills from roles (computed once since roles is static)
const getAllSkills = () => {
  const skillSet = new Set();
  roles.forEach(role => {
    if (role.skills) {
      role.skills.forEach(skill => {
        skillSet.add(skill.skillName);
      });
    }
  });
  return Array.from(skillSet);
};

// Get current role (most recent) - computed once since roles is static
const getCurrentRole = () => {
  return roles.find(role => role.dates && role.dates.includes('Present'));
};

// Compute these once since roles is a static import
const skills = getAllSkills();
const currentRole = getCurrentRole();

function StructuredData() {
  const location = useLocation();

  useEffect(() => {
    // Person Schema
    const personSchema = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Hadley Swello',
      jobTitle: currentRole ? currentRole.role : 'Senior Software Engineer',
      description:
        'Senior Software Engineer open to remote contract work. Specializing in Ruby on Rails, React, and modern web technologies. Remote-only developer available for contract opportunities.',
      url: BASE_URL,
      image: `${BASE_URL}/profile-picture.jpg`,
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Remote',
        addressCountry: 'US',
        addressRegion: 'Remote work, relocating to Uruguay',
      },
      sameAs: [
        'https://www.linkedin.com/in/hadleyswello/',
        'https://github.com/HadleySwello',
      ],
      knowsAbout: skills,
      alumniOf: {
        '@type': 'Organization',
        name: 'Poughkeepsie Child Development Center',
        description: 'Former Lead Preschool Teacher',
      },
    };

    // Add worksFor only if currentRole exists
    if (currentRole) {
      personSchema.worksFor = {
        '@type': 'Organization',
        name: currentRole.company,
        url: currentRole.link,
      };
    }

    // WebSite Schema
    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Hadley Swello Portfolio',
      url: BASE_URL,
      description:
        'Portfolio of Hadley Swello, a Senior Software Engineer and full-stack developer.',
      publisher: {
        '@type': 'Person',
        name: 'Hadley Swello',
      },
    };

    // BreadcrumbList Schema
    const breadcrumbItems = [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: BASE_URL,
      },
    ];

    if (location.pathname !== '/') {
      const pageNames = {
        '/about': 'About',
        '/experience': 'Experience',
        '/endorsements': 'Endorsements',
        '/widgets': 'Widgets',
        '/contact': 'Contact',
      };
      breadcrumbItems.push({
        '@type': 'ListItem',
        position: 2,
        name: pageNames[location.pathname] || 'Page',
        item: `${BASE_URL}${location.pathname}`,
      });
    }

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems,
    };

    // Game Schema for widgets/hangman page
    let gameSchema = null;
    if (location.pathname === '/widgets') {
      gameSchema = {
        '@context': 'https://schema.org',
        '@type': 'Game',
        name: 'Free Hangman Game',
        description:
          'Play a free online hangman word guessing game. Multiple word categories including tech terms, job skills, and personal interests. No downloads required.',
        gameLocation: `${BASE_URL}/widgets#hangman`,
        gamePlatform: 'Web Browser',
        applicationCategory: 'Game',
        operatingSystem: 'Web',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '5',
          ratingCount: '1',
        },
      };
    }

    // FAQ Schema for recruiter questions (on main pages)
    let faqSchema = null;
    if (
      location.pathname === '/' ||
      location.pathname === '/experience' ||
      location.pathname === '/contact'
    ) {
      faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Is Hadley Swello available for contract work?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, Hadley Swello is open to remote contract work opportunities as a Senior Software Engineer or comparable role.',
            },
          },
          {
            '@type': 'Question',
            name: "What is Hadley Swello's work location preference?",
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Hadley Swello is a remote-only developer and will be relocating to Uruguay soon. All work must be remote.',
            },
          },
          {
            '@type': 'Question',
            name: 'What technologies does Hadley Swello work with?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Hadley Swello specializes in Ruby on Rails, React, PostgreSQL, AWS, and modern web development technologies. See the experience page for a complete list of skills and technologies.',
            },
          },
          {
            '@type': 'Question',
            name: 'What level of software engineering experience does Hadley Swello have?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Hadley Swello is a Senior Software Engineer with experience at companies like Wayfair, Valor Performance, and Stay Healthy Zone. See the experience page for detailed career history.',
            },
          },
        ],
      };
    }

    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll(
      'script[type="application/ld+json"]'
    );
    existingScripts.forEach(script => script.remove());

    // Add Person schema
    const personScript = document.createElement('script');
    personScript.type = 'application/ld+json';
    personScript.text = JSON.stringify(personSchema);
    document.head.appendChild(personScript);

    // Add WebSite schema
    const websiteScript = document.createElement('script');
    websiteScript.type = 'application/ld+json';
    websiteScript.text = JSON.stringify(websiteSchema);
    document.head.appendChild(websiteScript);

    // Add BreadcrumbList schema
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    // Add Game schema if on widgets page
    if (gameSchema) {
      const gameScript = document.createElement('script');
      gameScript.type = 'application/ld+json';
      gameScript.text = JSON.stringify(gameSchema);
      document.head.appendChild(gameScript);
    }

    // Add FAQ schema if on relevant pages
    if (faqSchema) {
      const faqScript = document.createElement('script');
      faqScript.type = 'application/ld+json';
      faqScript.text = JSON.stringify(faqSchema);
      document.head.appendChild(faqScript);
    }

    // Cleanup function
    return () => {
      const scripts = document.querySelectorAll(
        'script[type="application/ld+json"]'
      );
      scripts.forEach(script => {
        if (
          script.textContent.includes('Person') ||
          script.textContent.includes('WebSite') ||
          script.textContent.includes('BreadcrumbList') ||
          script.textContent.includes('Game') ||
          script.textContent.includes('FAQPage')
        ) {
          script.remove();
        }
      });
    };
  }, [location.pathname]);

  return null;
}

export default StructuredData;
